/**
 * Session configuration model for the mock client simulation.
 *
 * The panel values below are assembled into a system prompt that will
 * eventually be sent to the language model. For now the prompt is only
 * previewed in the UI (see buildSystemPrompt); no network call is made.
 */

export interface ClientProfile {
	name: string;
	gender: "female" | "male" | "non-binary";
	age: number;
	presentingProblem: PresentingProblem;
	severity: "mild" | "moderate" | "severe";
	approach: ClinicalApproach;
	sessionLength: number; // minutes
	notes: string;
}

export type PresentingProblem =
	| "generalized_anxiety"
	| "depression"
	| "social_anxiety"
	| "panic_disorder"
	| "ptsd"
	| "grief"
	| "burnout";

export type ClinicalApproach =
	| "person_centered"
	| "cbt"
	| "psychodynamic"
	| "motivational_interviewing"
	| "solution_focused";

export const PRESENTING_PROBLEM_LABELS: Record<PresentingProblem, string> = {
	generalized_anxiety: "Generalized Anxiety Disorder",
	depression: "Major Depressive Disorder",
	social_anxiety: "Social Anxiety Disorder",
	panic_disorder: "Panic Disorder",
	ptsd: "Post-Traumatic Stress Disorder",
	grief: "Grief / Bereavement",
	burnout: "Occupational Burnout",
};

export const APPROACH_LABELS: Record<ClinicalApproach, string> = {
	person_centered: "Person-Centered Therapy",
	cbt: "Cognitive Behavioral Therapy (CBT)",
	psychodynamic: "Psychodynamic Therapy",
	motivational_interviewing: "Motivational Interviewing",
	solution_focused: "Solution-Focused Brief Therapy",
};

export const SEVERITY_LABELS: Record<ClientProfile["severity"], string> = {
	mild: "Mild",
	moderate: "Moderate",
	severe: "Severe",
};

export const DEFAULT_PROFILE: ClientProfile = {
	name: "Jordan",
	gender: "female",
	age: 29,
	presentingProblem: "generalized_anxiety",
	severity: "moderate",
	approach: "person_centered",
	sessionLength: 50,
	notes: "",
};

/**
 * Assemble the profiling fields into a system prompt describing the
 * simulated client. This is the artifact the panel is really shaping.
 */
export function buildSystemPrompt(profile: ClientProfile): string {
	const problem = PRESENTING_PROBLEM_LABELS[profile.presentingProblem];
	const approach = APPROACH_LABELS[profile.approach];
	const severity = SEVERITY_LABELS[profile.severity].toLowerCase();

	const lines = [
		"You are role-playing as a therapy client in a training simulation for",
		"mental-health professionals. Stay in character as the client at all times;",
		"do not break character, give clinical advice, or act as the therapist.",
		"",
		`Client name: ${profile.name}`,
		`Gender: ${profile.gender}`,
		`Age: ${profile.age}`,
		`Presenting problem: ${problem}`,
		`Symptom severity: ${severity}`,
		`Therapeutic modality the clinician will use: ${approach}`,
		`Planned session length: ${profile.sessionLength} minutes`,
	];

	if (profile.notes.trim()) {
		lines.push("", `Additional background: ${profile.notes.trim()}`);
	}

	lines.push(
		"",
		`Respond as the client would during a ${profile.sessionLength}-minute session,`,
		`with a symptom presentation consistent with ${severity} ${problem}.`,
		"Use natural, conversational language. Reveal information gradually,",
		"the way a real client would.",
	);

	return lines.join("\n");
}

export interface ChatMessage {
	id: string;
	role: "client" | "clinician";
	text: string;
	timestamp: number;
}

/** Placeholder opening line the simulated client "says" when a session starts. */
export function openingMessage(profile: ClientProfile): string {
	const problem = PRESENTING_PROBLEM_LABELS[profile.presentingProblem].toLowerCase();
	return `Hi. I'm ${profile.name}. I guess I'm here because of my ${problem}... I'm not really sure where to start.`;
}
